// =============================================================================
// VINUM - Order Store
// Migrated from Flutter OrderProvider with EXACT behavior
// =============================================================================

import { create } from 'zustand';
import type { Order, OrderFilter, OrderStatus } from '../types';
import { OrderAPI } from '../api';
import { PAGINATION } from '../constants';

// -----------------------------------------------------------------------------
// Types
// -----------------------------------------------------------------------------

interface OrderState {
  orders: Order[];
  selectedOrder: Order | null;
  isLoading: boolean;
  isLoadingMore: boolean;
  error: string | null;

  // Pagination
  page: number;
  totalPages: number;
  totalItems: number;
  hasMore: boolean;

  // Filter
  filter: OrderFilter;

  // Statistics
  statistics: {
    totalOrders: number;
    pendingOrders: number;
    completedOrders: number;
    cancelledOrders: number;
  } | null;
}

interface OrderActions {
  // Data loading
  loadOrders: (filter?: OrderFilter) => Promise<void>;
  loadMoreOrders: () => Promise<void>;
  loadOrder: (orderId: string) => Promise<Order>;
  refreshOrders: () => Promise<void>;

  // Filtering
  setFilter: (filter: Partial<OrderFilter>) => void;
  setStatusFilter: (status: OrderStatus | 'all') => void;
  clearFilter: () => void;

  // Order actions
  cancelOrder: (orderId: string, reason?: string) => Promise<void>;
  reorder: (orderId: string) => Promise<void>;

  // Statistics
  loadStatistics: () => Promise<void>;

  // State management
  setSelectedOrder: (order: Order | null) => void;
  clearError: () => void;
}

type OrderStore = OrderState & OrderActions;

// -----------------------------------------------------------------------------
// Initial State
// -----------------------------------------------------------------------------

const initialFilter: OrderFilter = {
  status: 'all',
  page: PAGINATION.DEFAULT_PAGE,
  limit: PAGINATION.DEFAULT_LIMIT,
};

// -----------------------------------------------------------------------------
// Store Implementation
// -----------------------------------------------------------------------------

export const useOrderStore = create<OrderStore>()((set, get) => ({
  // Initial state
  orders: [],
  selectedOrder: null,
  isLoading: false,
  isLoadingMore: false,
  error: null,

  page: 1,
  totalPages: 1,
  totalItems: 0,
  hasMore: false,

  filter: { ...initialFilter },
  statistics: null,

  // Actions

  loadOrders: async (filter?: OrderFilter) => {
    set({ isLoading: true, error: null });

    try {
      const appliedFilter = filter || get().filter;
      const response = await OrderAPI.getOrders({
        ...appliedFilter,
        page: 1,
      });

      set({
        orders: response.orders,
        page: response.page,
        totalPages: response.totalPages,
        totalItems: response.total,
        hasMore: response.page < response.totalPages,
        isLoading: false,
        filter: { ...appliedFilter, page: 1 },
      });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Errore caricamento ordini';
      set({ isLoading: false, error: message });
    }
  },

  loadMoreOrders: async () => {
    const { hasMore, isLoadingMore, filter, orders } = get();

    if (!hasMore || isLoadingMore) return;

    set({ isLoadingMore: true });

    try {
      const nextPage = (filter.page || 1) + 1;
      const response = await OrderAPI.getOrders({
        ...filter,
        page: nextPage,
      });

      set({
        orders: [...orders, ...response.orders],
        page: response.page,
        hasMore: response.page < response.totalPages,
        isLoadingMore: false,
        filter: { ...filter, page: nextPage },
      });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Errore caricamento';
      set({ isLoadingMore: false, error: message });
    }
  },

  loadOrder: async (orderId: string) => {
    set({ isLoading: true, error: null });

    try {
      const order = await OrderAPI.getOrder(orderId);
      set({ selectedOrder: order, isLoading: false });
      return order;
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Errore caricamento ordine';
      set({ isLoading: false, error: message });
      throw error;
    }
  },

  refreshOrders: async () => {
    const { filter } = get();
    await get().loadOrders({ ...filter, page: 1 });
  },

  setFilter: (newFilter: Partial<OrderFilter>) => {
    const currentFilter = get().filter;
    set({
      filter: { ...currentFilter, ...newFilter, page: 1 },
    });
  },

  setStatusFilter: (status: OrderStatus | 'all') => {
    get().setFilter({ status });
    get().loadOrders();
  },

  clearFilter: () => {
    set({ filter: { ...initialFilter } });
  },

  cancelOrder: async (orderId: string, reason?: string) => {
    set({ isLoading: true, error: null });

    try {
      const updatedOrder = await OrderAPI.cancelOrder(orderId, reason);

      // Update order in list
      const orders = get().orders.map((o) =>
        o.id === orderId ? updatedOrder : o
      );

      set({
        orders,
        selectedOrder: get().selectedOrder?.id === orderId ? updatedOrder : get().selectedOrder,
        isLoading: false,
      });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Errore annullamento ordine';
      set({ isLoading: false, error: message });
      throw error;
    }
  },

  reorder: async (orderId: string) => {
    set({ isLoading: true, error: null });

    try {
      await OrderAPI.reorder(orderId);
      set({ isLoading: false });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Errore riordino';
      set({ isLoading: false, error: message });
      throw error;
    }
  },

  loadStatistics: async () => {
    try {
      const statistics = await OrderAPI.getStatistics();
      set({ statistics });
    } catch {
      // Silently fail, statistics are optional
    }
  },

  setSelectedOrder: (order: Order | null) => {
    set({ selectedOrder: order });
  },

  clearError: () => {
    set({ error: null });
  },
}));

// -----------------------------------------------------------------------------
// Selectors
// -----------------------------------------------------------------------------

export const selectOrders = (state: OrderStore) => state.orders;
export const selectSelectedOrder = (state: OrderStore) => state.selectedOrder;
export const selectOrderIsLoading = (state: OrderStore) => state.isLoading;
export const selectOrderError = (state: OrderStore) => state.error;
export const selectOrderFilter = (state: OrderStore) => state.filter;
export const selectOrderHasMore = (state: OrderStore) => state.hasMore;
export const selectOrderStatistics = (state: OrderStore) => state.statistics;

// Filter orders by status
export const selectOrdersByStatus = (status: OrderStatus | 'all') => (state: OrderStore) => {
  if (status === 'all') return state.orders;
  return state.orders.filter((o) => o.status === status);
};
